import { Icon } from "@iconify/react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { apiRequest } from "../../redux/api";
import { languageSwitcherOptions, LanguageCode } from "../../localization/supportedLanguages";
import { setLanguage } from "../../redux/features/language/languageSlice";
import { RootState } from "../../redux/store";
import { buildAppLink, redirectForAttachment, redirectForChat } from "../../utils/landingRedirect";
import "./YouWoConsumerLanding.css";
import YouWoConsumerWorkflowDemo from "./YouWoConsumerWorkflowDemo";

type StripePrice = {
  amount: number | null;
  currency: string;
  interval?: string;
};

type StripeTier = {
  monthly?: StripePrice | null;
  yearly?: StripePrice | null;
};

type StripePricesResponse = {
  currency?: string;
  tiers?: {
    plus?: StripeTier;
    pro?: StripeTier;
    max?: StripeTier;
  };
};

type ConsumerPlanId = "free" | "plus" | "pro" | "max";
type PaidConsumerPlanId = Exclude<ConsumerPlanId, "free" | "max">;

type ConsumerCopy = {
  nav: {
    product: string;
    pricing: string;
    signIn: string;
    start: string;
    languageLabel: string;
  };
  hero: {
    titleStart: string;
    titleAccent: string;
    titleEnd: string;
    lead: string;
    placeholder: string;
    starterPrompt: string;
    send: string;
    attach: string;
  };
  proof: string[];
  availability: {
    label: string;
    ios: string;
    desktop: string;
  };
  flow: {
    kicker: string;
    cards: Array<{ icon: string; title: string }>;
  };
  pricing: {
    kicker: string;
    title: string;
    body: string;
    monthly: string;
    yearly: string;
    perMonth: string;
    perYear: string;
    billedMonthly: string;
    billedYearlyPrefix: string;
    billedYearlySuffix: string;
    yearlySaveSuffix: string;
    startFree: string;
    upgrade: string;
    subscribe: string;
    popular: string;
    plans: Record<ConsumerPlanId, {
      name: string;
      subtitle: string;
      price?: string;
      features: string[];
    }>;
  };
};

const consumerCopy = {
  en: {
    nav: {
      product: "What it does",
      pricing: "Pricing",
      signIn: "Sign in",
      start: "Start free",
      languageLabel: "Switch language",
    },
    hero: {
      titleStart: "Capture your context.",
      titleAccent: "Let YouWo do the work.",
      titleEnd: "",
      lead: "",
      placeholder: "What should YouWo work on?",
      starterPrompt: "Find the context and prepare my next step.",
      send: "Start",
      attach: "Add sources",
    },
    proof: ["Record", "Import", "Agent", "Create", "Study"],
    availability: {
      label: "Also available",
      ios: "Download iOS",
      desktop: "Download desktop",
    },
    flow: {
      kicker: "How it feels",
      cards: [
        { icon: "lucide:upload-cloud", title: "Capture context" },
        { icon: "lucide:brain", title: "Find what matters" },
        { icon: "lucide:sparkles", title: "Move to the next step" },
      ],
    },
    pricing: {
      kicker: "Pricing",
      title: "Start free. Upgrade when your workload gets bigger.",
      body: "Monthly and yearly plans use the same credit model as the app. Yearly shows the effective monthly price and the billed yearly amount.",
      monthly: "Monthly",
      yearly: "Yearly",
      perMonth: "CAD / month",
      perYear: "/ year",
      billedMonthly: "Billed monthly",
      billedYearlyPrefix: "Billed",
      billedYearlySuffix: "yearly",
      yearlySaveSuffix: "off",
      startFree: "Start free",
      upgrade: "Upgrade",
      subscribe: "購読",
      popular: "Popular",
      plans: {
        free: {
          name: "Free",
          subtitle: "Try YouWo with daily credits",
          price: "0",
          features: ["500 daily refresh credits", "Basic AI help", "Ask about meetings, docs, and notes", "Great for trying YouWo with daily credits"],
        },
        plus: {
          name: "Plus",
          subtitle: "21,000 monthly AI credits for everyday work",
          features: ["21,000 monthly AI credits", "Auto model routing with Plus-tier capacity", "Study tools included and billed from credits", "Recording, translation, and imports are billed from credits", "Larger uploads, 100-page PDFs, and source-backed tools", "1 workflow automation"],
        },
        pro: {
          name: "Pro",
          subtitle: "55,000 monthly AI credits with advanced models and higher limits",
          features: ["All Plus benefits", "55,000 monthly AI credits", "Explicit model picker with advanced research models", "Fast transcription and translation use credits", "3 workflow automations", "Higher document, image, and audio import limits"],
        },
        max: {
          name: "Max",
          subtitle: "For power users with very high volume",
          features: ["1,200,000 monthly credits", "Highest public model access", "More room for large projects", "Built for intensive AI workflows"],
        },
      },
    },
  },
  zh: {
    nav: {
      product: "功能",
      pricing: "价格",
      signIn: "登录",
      start: "免费开始",
      languageLabel: "切换语言",
    },
    hero: {
      titleStart: "捕捉上下文。",
      titleAccent: "让 YouWo 做完工作。",
      titleEnd: "",
      lead: "",
      placeholder: "你想让 YouWo 做什么？",
      starterPrompt: "找到上下文，并准备我的下一步。",
      send: "开始",
      attach: "添加来源",
    },
    proof: ["录音", "导入", "Agent", "创作", "学习"],
    availability: {
      label: "也可以下载",
      ios: "下载 iOS",
      desktop: "下载桌面端",
    },
    flow: {
      kicker: "使用感受",
      cards: [
        { icon: "lucide:upload-cloud", title: "捕捉上下文" },
        { icon: "lucide:brain", title: "找到重点" },
        { icon: "lucide:sparkles", title: "推进下一步" },
      ],
    },
    pricing: {
      kicker: "价格",
      title: "免费开始，任务变多后再升级",
      body: "月付和年付沿用 app 内同一套 credit 模型。年付显示折算后的月费和实际年付金额",
      monthly: "月付",
      yearly: "年付",
      perMonth: "CAD / 月",
      perYear: "/ 年",
      billedMonthly: "按月收取",
      billedYearlyPrefix: "每年收取",
      billedYearlySuffix: "",
      yearlySaveSuffix: "优惠",
      startFree: "免费开始",
      upgrade: "升级",
      subscribe: "订阅",
      popular: "推荐",
      plans: {
        free: {
          name: "Free",
          subtitle: "用每日 credits 试用 YouWo",
          price: "0",
          features: ["每天 500 credits", "基础 AI 帮助", "围绕会议、文档和记录提问", "适合尝试一个工作流"],
        },
        plus: {
          name: "Plus",
          subtitle: "21,000 monthly AI credits，适合日常工作",
          features: ["每月 21,000 AI credits", "Plus 模型用于更快的日常工作", "无限总结、闪卡和测验", "录音、翻译和导入会从 credits 扣除", "更大的上传额度和带来源工具", "1 个 workflow automation"],
        },
        pro: {
          name: "Pro",
          subtitle: "55,000 monthly AI credits，包含高级模型和更高额度",
          features: ["包含所有 Plus benefits", "每月 55,000 AI credits", "高级模型用于研究和带引用工作", "高速转录和翻译使用 credits", "3 个 workflow automations", "更高的文档、图片和音频导入额度"],
        },
        max: {
          name: "Max",
          subtitle: "适合高频重度用户",
          features: ["每月 1,200,000 credits", "最高公开模型访问", "大型项目空间更充足", "适合密集 AI 工作流"],
        },
      },
    },
  },
  ja: {
    nav: {
      product: "できること",
      pricing: "料金",
      signIn: "ログイン",
      start: "無料で始める",
      languageLabel: "言語を切り替え",
    },
    hero: {
      titleStart: "文脈を取り込む。",
      titleAccent: "作業は YouWo に。",
      titleEnd: "",
      lead: "",
      placeholder: "YouWo に何を任せますか？",
      starterPrompt: "文脈を見つけて、次の一手を準備して。",
      send: "始める",
      attach: "ソースを追加",
    },
    proof: ["録音", "取り込み", "Agent", "作成", "学習"],
    availability: {
      label: "アプリでも利用可能",
      ios: "iOS をダウンロード",
      desktop: "デスクトップ版をダウンロード",
    },
    flow: {
      kicker: "使い心地",
      cards: [
        { icon: "lucide:upload-cloud", title: "文脈を取り込む" },
        { icon: "lucide:brain", title: "重要点を見つける" },
        { icon: "lucide:sparkles", title: "次の一手へ進む" },
      ],
    },
    pricing: {
      kicker: "料金",
      title: "無料で始めて、必要になったらアップグレード",
      body: "月額と年額はアプリと同じ credit モデルです。年額は月あたり換算と年間請求額を表示します",
      monthly: "月額",
      yearly: "年額",
      perMonth: "CAD / 月",
      perYear: "/ 年",
      billedMonthly: "月額請求",
      billedYearlyPrefix: "年額",
      billedYearlySuffix: "請求",
      yearlySaveSuffix: "OFF",
      startFree: "無料で始める",
      upgrade: "アップグレード",
      subscribe: "Subscribe to",
      popular: "おすすめ",
      plans: {
        free: {
          name: "Free",
          subtitle: "毎日の credits で YouWo を試す",
          price: "0",
          features: ["毎日 500 credits", "基本 AI サポート", "会議、文書、メモについて質問", "ひとつのワークフローを試すのに最適"],
        },
        plus: {
          name: "Plus",
          subtitle: "日常業務向けの 21,000 monthly AI credits",
          features: ["毎月 21,000 AI credits", "日常業務を速くする Plus モデルアクセス", "要約、単語カード、クイズは無制限", "録音、翻訳、取り込みは credits を使用", "より大きなアップロードと根拠付きツール", "1 workflow automation"],
        },
        pro: {
          name: "Pro",
          subtitle: "高度なモデルと上限を含む 55,000 monthly AI credits",
          features: ["Plus のすべての benefits", "毎月 55,000 AI credits", "研究と引用付き作業向けの高度なモデル", "高速文字起こしと翻訳は credits を使用", "3 workflow automations", "文書、画像、音声の取り込み上限アップ"],
        },
        max: {
          name: "Max",
          subtitle: "大量に使うパワーユーザー向け",
          features: ["毎月 1,200,000 credits", "公開プラン最高のモデルアクセス", "大規模プロジェクトに余裕", "集中的な AI ワークフロー向け"],
        },
      },
    },
  },
} satisfies Record<LanguageCode, ConsumerCopy>;

const fallbackMonthlyPrices: Record<string, Record<Exclude<ConsumerPlanId, "free">, number>> = {
  USD: { plus: 1999, pro: 4999, max: 9999 },
  CAD: { plus: 2499, pro: 5999, max: 11999 },
  CNY: { plus: 6800, pro: 19800, max: 49900 },
};

const fallbackYearlyPrices: Record<string, Record<Exclude<ConsumerPlanId, "free">, number>> = {
  USD: { plus: 19999, pro: 49999, max: 99999 },
  CAD: { plus: 24999, pro: 59999, max: 119999 },
  CNY: { plus: 69800, pro: 199800, max: 499000 },
};

const currencySymbol = (currency: string) => {
  if (currency.toUpperCase() === "CNY") return "¥";
  if (currency.toUpperCase() === "CAD") return "CA$";
  return "$";
};

const formatCents = (amount: number) => {
  const rounded = Math.round(amount);
  const dollars = Math.floor(rounded / 100);
  const cents = rounded % 100;
  return cents > 0 ? `${dollars}.${String(cents).padStart(2, "0")}` : `${dollars}`;
};

const getTierAmount = (
  tier: Exclude<ConsumerPlanId, "free">,
  interval: "monthly" | "yearly",
  prices: StripePricesResponse | null,
) => {
  const apiAmount = prices?.tiers?.[tier]?.[interval]?.amount;
  if (typeof apiAmount === "number") {
    return apiAmount;
  }

  const currency = prices?.currency?.toUpperCase() || "CAD";
  const table = interval === "monthly" ? fallbackMonthlyPrices : fallbackYearlyPrices;
  return (table[currency] || table.USD)[tier];
};

const getYearlyMonthlyEquivalent = (yearlyAmount: number) => Math.floor(yearlyAmount / 12);

const getYearlySavings = (
  monthlyAmount: number,
  yearlyAmount: number,
) => Math.max(0, Math.round((1 - yearlyAmount / (monthlyAmount * 12)) * 100));

const logoMaskStyle: React.CSSProperties = {
  WebkitMask: 'url("/logo/logo.png") center / contain no-repeat',
  mask: 'url("/logo/logo.png") center / contain no-repeat',
};

const YouWoConsumerLanding: React.FC = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const copy: ConsumerCopy = consumerCopy[currentLanguage] ?? consumerCopy.en;
  const [prompt, setPrompt] = useState("");
  const [languageOpen, setLanguageOpen] = useState(false);
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">("monthly");
  const [prices, setPrices] = useState<StripePricesResponse | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await apiRequest({
          url: "subscriptions/stripe-prices-public",
          method: "GET",
          useAuthHeader: false,
        });

        if (!cancelled) {
          setPrices(response);
        }
      } catch {
        if (!cancelled) {
          setPrices({ currency: "CAD" });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const currency = prices?.currency?.toUpperCase() || "CAD";
  const visiblePlans = useMemo<PaidConsumerPlanId[]>(() => ["plus", "pro"], []);

  const handleLanguageChange = (language: LanguageCode) => {
    dispatch(setLanguage(language));
    i18n.changeLanguage(language);
    setLanguageOpen(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    redirectForChat(prompt.trim() || copy.hero.starterPrompt);
  };

  return (
    <main className="yw-consumer-page" data-lang={currentLanguage}>
      <nav className="yw-consumer-nav" aria-label="YouWo consumer landing navigation">
        <a className="yw-consumer-brand" href="/internal/youwo-consumer-landing">
          <span aria-hidden="true" className="yw-consumer-logo-mark" style={logoMaskStyle} />
          <strong>YouWo</strong>
        </a>
        <div className="yw-consumer-nav-links">
          <a href="#product">{copy.nav.product}</a>
          <a href="#pricing">{copy.nav.pricing}</a>
        </div>
        <div className="yw-consumer-nav-actions">
          <div className="yw-consumer-language-menu">
            <button
              aria-expanded={languageOpen}
              aria-label={copy.nav.languageLabel}
              className="yw-consumer-language-trigger"
              onClick={() => setLanguageOpen((isOpen) => !isOpen)}
              type="button"
            >
              {languageSwitcherOptions.find((option) => option.code === currentLanguage)?.shortLabel ?? "EN"}
              <Icon icon="lucide:chevron-down" />
            </button>
            {languageOpen ? (
              <div className="yw-consumer-language-popover">
                {languageSwitcherOptions.map((option) => (
                  <button
                    className={option.code === currentLanguage ? "yw-consumer-language-option yw-consumer-language-option-active" : "yw-consumer-language-option"}
                    key={option.code}
                    onClick={() => handleLanguageChange(option.code)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <a className="yw-consumer-start" href={buildAppLink()}>{copy.nav.start}</a>
        </div>
      </nav>

      <section className="yw-consumer-hero">
        <div className="yw-consumer-hero-copy">
          <h1>
            {copy.hero.titleStart}{" "}
            <span>{copy.hero.titleAccent}</span>{" "}
            {copy.hero.titleEnd}
          </h1>
          {copy.hero.lead ? <p>{copy.hero.lead}</p> : null}
        </div>

        <form className="yw-capture-box" onSubmit={handleSubmit}>
          <textarea
            aria-label={copy.hero.placeholder}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder={copy.hero.placeholder}
            rows={3}
            value={prompt}
          />
          <div className="yw-capture-footer">
            <button className="yw-capture-attach" onClick={redirectForAttachment} type="button">
              <Icon icon="lucide:paperclip" />
              {copy.hero.attach}
            </button>
            <button className="yw-capture-submit" type="submit">
              {copy.hero.send}
              <Icon icon="lucide:arrow-up" />
            </button>
          </div>
        </form>

        <div className="yw-platform-row" aria-label={copy.availability.label}>
          <span>{copy.availability.label}</span>
          <a href="https://apps.apple.com/app/youwo-ai-agent/id6760689478" target="_blank" rel="noopener noreferrer">
            <Icon icon="logos:apple" />
            {copy.availability.ios}
          </a>
          <a href="https://downloads.youwo.ai/mac/YouWo-latest.dmg" target="_blank" rel="noopener noreferrer">
            <Icon icon="lucide:monitor-down" />
            {copy.availability.desktop}
          </a>
        </div>
      </section>

      <YouWoConsumerWorkflowDemo id="product" />

      <section className="yw-flow-section">
        <p>{copy.flow.kicker}</p>
        <div className="yw-flow-grid">
          {copy.flow.cards.map((card) => (
            <article key={card.title}>
              <Icon icon={card.icon} />
              <h3>{card.title}</h3>
            </article>
          ))}
        </div>
      </section>

      <section className="yw-pricing-section" id="pricing">
        <div className="yw-section-heading">
          <p>{copy.pricing.kicker}</p>
          <h2>{copy.pricing.title}</h2>
          <span>{copy.pricing.body}</span>
        </div>
        <div className="yw-billing-toggle" role="group" aria-label="Billing interval">
          <button
            className={billingInterval === "monthly" ? "yw-billing-active" : ""}
            onClick={() => setBillingInterval("monthly")}
            type="button"
          >
            {copy.pricing.monthly}
          </button>
          <button
            className={billingInterval === "yearly" ? "yw-billing-active" : ""}
            onClick={() => setBillingInterval("yearly")}
            type="button"
          >
            {copy.pricing.yearly}
          </button>
        </div>

        <div className="yw-price-grid">
          {visiblePlans.map((planId) => {
            const planCopy = copy.pricing.plans[planId];
            const monthlyAmount = getTierAmount(planId, "monthly", prices);
            const yearlyAmount = getTierAmount(planId, "yearly", prices);
            const displayAmount = billingInterval === "monthly" ? monthlyAmount : getYearlyMonthlyEquivalent(yearlyAmount);
            const priceLabel = `${currencySymbol(currency)}${formatCents(displayAmount)}`;
            const billingNote = billingInterval === "monthly"
              ? copy.pricing.billedMonthly
              : `${copy.pricing.billedYearlyPrefix} ${currencySymbol(currency)}${formatCents(yearlyAmount)} ${copy.pricing.billedYearlySuffix}`.trim();
            const yearlySavings = getYearlySavings(monthlyAmount, yearlyAmount);

            return (
              <article className={planId === "plus" ? "yw-price-card yw-price-card-popular" : "yw-price-card"} key={planId}>
                {planId === "plus" ? <span className="yw-popular-pill">{copy.pricing.popular}</span> : null}
                <h3>{planCopy.name}</h3>
                <p>{planCopy.subtitle}</p>
                <div className="yw-price-value">
                  <strong>{priceLabel}</strong>
                  <span>{copy.pricing.perMonth}</span>
                </div>
                <p className="yw-price-billing">
                  {billingNote}
                  {billingInterval === "yearly" ? <span>{yearlySavings}% {copy.pricing.yearlySaveSuffix}</span> : null}
                </p>
                <ul>
                  {planCopy.features.map((feature) => (
                    <li key={feature}><Icon icon="lucide:check" />{feature}</li>
                  ))}
                </ul>
                <a href={buildAppLink("/pricing")}>{copy.pricing.subscribe} {planCopy.name}</a>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default YouWoConsumerLanding;
